// src/pages/StockMaterial/AddStockMaterial.js
// ✅ Completely standalone — uses Stock Material APIs only
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaSave, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../services/api';
import Notes from './Notes';
import Details from './Details';
import './StockMaterial.css';

const AddStockMaterial = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shopId } = useParams();
  const editData = location.state?.editData;
  const isEditMode = !!editData;

  const detailsRef = useRef();
  const [activeTab, setActiveTab] = useState('details');
  const [savedMaterialId, setSavedMaterialId] = useState(editData?.id || null);

  // FORM STATE
  const [receivedDate, setReceivedDate] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [materialSpecification, setMaterialSpecification] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [materialLotNum, setMaterialLotNum] = useState('');
  const [internalLotNumber, setInternalLotNumber] = useState('');
  const [materialType, setMaterialType] = useState('');
  const [unit, setUnit] = useState('');
  const [quantityReceived, setQuantityReceived] = useState('');
  const [faultPieces, setFaultPieces] = useState('');
  const [afterInspectionFinalStock, setAfterInspectionFinalStock] = useState('');

  // Shop & City
  const [shops, setShops] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch shops
  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      const response = await api.get('/user/shops/');
      setShops(response.data);
      if (shopId && !isEditMode) setSelectedShop(shopId);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  // Fetch cities
  useEffect(() => {
    if (selectedShop) fetchCities(selectedShop);
    else setCities([]);
  }, [selectedShop]);

  const fetchCities = async (shopIdParam) => {
    try {
      const response = await api.get(`/inventory/shop-cities/?shop_id=${shopIdParam}`);
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  // Edit mode - populate form
  useEffect(() => {
    if (isEditMode && editData) {
      setReceivedDate(editData.received_date || '');
      setInvoiceNo(editData.invoice_no || '');
      setMaterialSpecification(editData.material_specification || '');
      setSupplierName(editData.supplier_name || '');
      setReferenceNo(editData.reference_no || '');
      setMaterialLotNum(editData.material_lot_num || '');
      setInternalLotNumber(editData.internal_lot_number || '');
      setMaterialType(editData.material_type || '');
      setUnit(editData.unit || '');
      setQuantityReceived(editData.quantity_received || '');
      setFaultPieces(editData.fault_pieces || '');
      setAfterInspectionFinalStock(
        editData.after_inspection_final_stock || editData.after_inspection || ''
      );
      setSelectedShop(editData.shop_id || editData.shop?.shop_id || '');
      setSelectedCity(editData.city || '');
      if (editData.id) setSavedMaterialId(editData.id);
    }
  }, [isEditMode, editData]);

  // Auto-calculate
  useEffect(() => {
    const qty = parseFloat(quantityReceived) || 0;
    const fault = parseFloat(faultPieces) || 0;
    const result = qty - fault;
    setAfterInspectionFinalStock(result >= 0 ? result.toString() : '0');
  }, [quantityReceived, faultPieces]);

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!receivedDate) return Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please select received date', confirmButtonColor: '#004aad' });
    if (!invoiceNo.trim()) return Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please enter invoice number', confirmButtonColor: '#004aad' });
    if (!materialSpecification.trim()) return Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please enter material specification', confirmButtonColor: '#004aad' });
    if (!supplierName.trim()) return Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please enter supplier name', confirmButtonColor: '#004aad' });
    if (!materialLotNum.trim()) return Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please enter material lot number', confirmButtonColor: '#004aad' });
    if (!unit.trim()) return Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please enter unit', confirmButtonColor: '#004aad' });
    if (!quantityReceived) return Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Please enter quantity received', confirmButtonColor: '#004aad' });
    if (!selectedShop) return Swal.fire({ icon: 'warning', title: 'Shop Required', text: 'Please select a shop', confirmButtonColor: '#004aad' });

    const formData = {
      received_date: receivedDate,
      invoice_no: invoiceNo.trim(),
      material_specification: materialSpecification.trim(),
      supplier_name: supplierName.trim(),
      reference_no: referenceNo.trim(),
      material_lot_num: materialLotNum.trim(),
      internal_lot_number: internalLotNumber.trim(),
      material_type: materialType.trim(),
      unit: unit.trim(),
      quantity_received: parseFloat(quantityReceived) || 0,
      fault_pieces: parseFloat(faultPieces) || 0,
      after_inspection_final_stock: parseFloat(afterInspectionFinalStock) || 0,
      shop_id: selectedShop,
      city: selectedCity || null,
    };

    setLoading(true);
    let newMaterialId = savedMaterialId;

    try {
      if (isEditMode) {
        const res = await api.put(`/inventory/stock-material/${editData.id}/update/`, formData);
        newMaterialId = res?.data?.id || editData.id;
      } else {
        const res = await api.post('/inventory/stock-material/', formData);
        newMaterialId = res?.data?.id || null;
      }

      if (newMaterialId) setSavedMaterialId(newMaterialId);

      let filesUploaded = 0;
      if (newMaterialId && detailsRef.current?.hasUnsavedFiles?.()) {
        console.log('📤 Auto-uploading pending files...');
        const uploadResult = await detailsRef.current.saveFiles(newMaterialId);
        if (uploadResult?.success) filesUploaded = uploadResult.uploaded || 0;
      }

      Swal.fire({
        icon: 'success',
        title: isEditMode ? 'Updated!' : 'Saved!',
        text: filesUploaded > 0
          ? `Stock material saved successfully! (${filesUploaded} file(s) uploaded)`
          : (isEditMode ? 'Stock material updated successfully!' : 'Stock material saved successfully!'),
        timer: 2000,
        showConfirmButton: true,
        confirmButtonColor: '#004aad',
      });

      setLoading(false);
    } catch (apiError) {
      console.error('❌ Save error:', apiError);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: apiError?.response?.data?.error || apiError.message || 'Something went wrong',
        confirmButtonColor: '#dc2626',
      });
      setLoading(false);
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: 'Reset Form?',
      text: 'All entered data will be cleared.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      cancelButtonColor: '#dc3545',
      confirmButtonText: 'Yes, reset it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        setReceivedDate('');
        setInvoiceNo('');
        setMaterialSpecification('');
        setSupplierName('');
        setReferenceNo('');
        setMaterialLotNum('');
        setInternalLotNumber('');
        setMaterialType('');
        setUnit('');
        setQuantityReceived('');
        setFaultPieces('');
        setAfterInspectionFinalStock('');
        setSelectedCity('');
        Swal.fire({ icon: 'success', title: 'Reset!', timer: 1500, showConfirmButton: false });
      }
    });
  };

  const handleCancel = () => navigate(`/shop/${selectedShop || shopId}/StockMaterial`);
  const handleBack = () => navigate(`/shop/${selectedShop || shopId}/StockMaterial`);

  if (loading) return <div className="loading">Saving...</div>;

  return (
    <div className="add-issuestock-container add-stockmaterial-page">
      <div className="form-header-section">
        <div className="header-content">
          <div>
            <h2>{isEditMode ? 'Edit Stock Material' : 'Add Stock Material'}</h2>
          </div>
        </div>
        <div className="back-button-wrapper">
          <button className="back-button" onClick={handleBack}>Back</button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="issuestock-form-container">
       {/* Shop & City */}
<div className="form-section">
  <h3 className="section-title">Shop Information</h3>
  <div className="asm-grid">
    <div className="form-group asm-field">
      <label>Shop <span className="required">*</span></label>
      <select value={selectedShop} onChange={(e) => setSelectedShop(e.target.value)} required className="form-input">
        <option value="">Select Shop</option>
        {shops.map(shop => (
          <option key={shop.shop_id} value={shop.shop_id}>{shop.shop_name}</option>
        ))}
      </select>
    </div>
    <div className="form-group asm-field">
      <label>City</label>
      <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} className="form-input" disabled={!selectedShop}>
        <option value="">Select City</option>
        {cities.map(city => (<option key={city} value={city}>{city}</option>))}
      </select>
    </div>

    {/* ✅ NEW: Empty placeholder to fill 3rd column — alignment fix */}
    <div className="form-group asm-field asm-empty-field"></div>
  </div>
</div>

        {/* Material Info — 3 fields per row */}
        <div className="form-section">
          <h3 className="section-title">Material Information</h3>
          <div className="asm-grid">
            <div className="form-group asm-field">
              <label>Received Date <span className="required">*</span></label>
              <input type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} className="form-input" required />
            </div>
            <div className="form-group asm-field">
              <label>Invoice No <span className="required">*</span></label>
              <input type="text" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="e.g. 37280" required className="form-input" />
            </div>
            <div className="form-group asm-field">
              <label>Supplier Name <span className="required">*</span></label>
              <input type="text" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} required className="form-input" />
            </div>

            <div className="form-group asm-field">
              <label>Reference No</label>
              <input type="text" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} className="form-input" />
            </div>
            <div className="form-group asm-field">
              <label>Material Lot Num <span className="required">*</span></label>
              <input type="text" value={materialLotNum} onChange={(e) => setMaterialLotNum(e.target.value)} required className="form-input" />
            </div>
            <div className="form-group asm-field">
              <label>Internal Lot Number</label>
              <input type="text" value={internalLotNumber} onChange={(e) => setInternalLotNumber(e.target.value)} className="form-input" />
            </div>

            <div className="form-group asm-field">
              <label>Material Type</label>
              <input type="text" value={materialType} onChange={(e) => setMaterialType(e.target.value)} className="form-input" />
            </div>
            <div className="form-group asm-field">
              <label>Unit <span className="required">*</span></label>
              <input type="text" value={unit} onChange={(e) => setUnit(e.target.value)} required className="form-input" />
            </div>
            <div className="form-group asm-field">
              <label>Material Specification <span className="required">*</span></label>
              <textarea value={materialSpecification} onChange={(e) => setMaterialSpecification(e.target.value)} rows="1" required className="form-textarea" />
            </div>
          </div>
        </div>

        {/* Quantity — 3 fields in one row */}
        <div className="form-section">
          <h3 className="section-title">Quantity & Inspection</h3>
          <div className="asm-grid">
            <div className="form-group asm-field">
              <label>Quantity Received <span className="required">*</span></label>
              <input type="number" step="any" min="0" value={quantityReceived} onChange={(e) => setQuantityReceived(e.target.value)} required className="form-input" />
            </div>
            <div className="form-group asm-field">
              <label>Fault Pieces</label>
              <input type="number" step="any" min="0" value={faultPieces} onChange={(e) => setFaultPieces(e.target.value)} className="form-input" />
            </div>
            <div className="form-group asm-field">
              <label>After Inspection Final Stock <span className="auto-badge-inline">AUTO</span></label>
              <input type="text" value={afterInspectionFinalStock} className="form-input remaining-stock" readOnly disabled />
              <small className="form-help-text">Auto = Quantity Received − Fault Pieces</small>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions form-actions-top">
          <button type="button" className="btn-cancel" onClick={handleCancel} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
           {isEditMode ? 'Update Material' : 'Save Material'}
          </button>
        </div>

        {/* Files & Notes tabs */}
        <div className="details-notes-section">
          <div className="details-notes-tabs">
            <button type="button" className={`details-notes-tab ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
              Details (Files)
            </button>
            <button type="button" className={`details-notes-tab ${activeTab === 'notes' ? 'active' : ''}`} onClick={() => setActiveTab('notes')}>
              Notes
            </button>
          </div>

          <div className="details-notes-content">
            <div style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
              <Details
                ref={detailsRef}
                trackingId={savedMaterialId || 'new'}
                mode="stock_material"
                isNew={!savedMaterialId}
                isEdit={isEditMode}
              />
            </div>
            <div style={{ display: activeTab === 'notes' ? 'block' : 'none' }}>
              <Notes trackingId={savedMaterialId || 'new'} mode="stock_material" />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddStockMaterial;